import * as z from "zod";

/**
 * The API's limit on a favorite's title. Mirrored here so an over-long title fails in the
 * form instead of on the request — see `FavoriteDocumentService.MaxTitleLength`.
 */
export const MAX_FAVORITE_TITLE_LENGTH = 255;

export const FAVORITE_TITLE_EMPTY_MESSAGE = "Udfyld navn";

/**
 * Schema for the rename form's single field.
 *
 * A favorite's title is only a label in the user's own favorites list — it is never a
 * file name and never reaches a filesystem — so unlike a workspace document's name
 * (see `documentRename.ts`) it may hold any character, and two favorites may share a
 * title. Blank and over-long are the only refusals, and they match the API's.
 */
export const renameFavoriteSchema = z.object({
  title: z
    .string()
    .refine((value) => value.trim().length > 0, {
      message: FAVORITE_TITLE_EMPTY_MESSAGE,
    })
    .refine((value) => value.trim().length <= MAX_FAVORITE_TITLE_LENGTH, {
      message: `Navn må højst være ${MAX_FAVORITE_TITLE_LENGTH} tegn`,
    }),
});

export type RenameFavoriteFormValues = z.infer<typeof renameFavoriteSchema>;

/**
 * The title to send for a favorite renamed to `title`. Surrounding whitespace carries no
 * meaning, so it is dropped — the same normalization the API applies before storing.
 */
export const buildFavoriteTitle = (title: string): string => title.trim();

/**
 * Whether the edited title is a real change. A rename to the current title is a no-op the
 * API stores nothing for, so the dialog closes without a request.
 */
export const isFavoriteTitleChanged = (
  currentTitle: string,
  title: string
): boolean => buildFavoriteTitle(title) !== currentTitle.trim();
